import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  Briefcase,
  MapPin,
  Clock,
  DollarSign,
  GraduationCap,
  Building,
  Calendar,
  Mail,
  Wifi,
  Book,
  Star,
  BookmarkPlus,
  Share2,
  ArrowLeft,
  Users,
} from "lucide-react";
import { useAuth } from "./context/AuthContext";
import { translateJob } from './utils/translator';

export const API_URL =
  process.env.REACT_APP_API_URL || "https://sbg-backend.onrender.com";

const JobDetails = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const { currentUser, saveJob } = useAuth();
  const currentLanguage = i18n.language;

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [translating, setTranslating] = useState(false);

  const handleSaveJob = async () => {
    if (!currentUser) {
      alert(t('jobs.details.loginToSave'));
      return;
    }
    try {
      await saveJob(id);
      setIsSaved(true);
      alert(t('jobs.details.saveSuccess'));
    } catch (error) {
      console.error("Error saving job:", error);
      alert(t('jobs.details.saveError'));
    }
  };

  useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/jobs/${id}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        
        // Translate job if needed
        if (currentLanguage !== 'en') {
          setTranslating(true);
          const translatedJob = await translateJob(data, currentLanguage);
          setJob(translatedJob);
          setTranslating(false);
        } else {
          setJob(data);
        }
      } catch (error) {
        console.error("Error fetching job details:", error);
        setError(t('jobs.details.error'));
      } finally {
        setLoading(false);
      }
    };

    fetchJobDetails();
  }, [id, currentLanguage, t]);

  // Helper function to get localized content
  const getLocalizedContent = (content) => {
    if (!content) return '';
    
    // If content is already a string, return it
    if (typeof content === 'string') return content;
    
    // If content has language versions, return the current language or fallback to English
    if (typeof content === 'object') {
      return content[currentLanguage] || content.en || '';
    }
    
    return '';
  };

  const renderField = (icon, label, value) => {
    if (!value && value !== 0) return null;
    return (
      <div className="flex items-center mb-4 bg-gray-100 p-3 rounded-lg">
        {icon}
        <span className="font-semibold mr-2">{label}:</span>
        <span>{value}</span>
      </div>
    );
  };

  const renderRequirements = (requirements) => {
    if (!requirements) return null;

    let requirementsList = [];
    if (typeof requirements === "string") {
      requirementsList = requirements.split("\n");
    } else if (Array.isArray(requirements)) {
      requirementsList = requirements;
    } else {
      return <p className="text-gray-700">{requirements}</p>;
    }

    return (
      <ul className="list-disc pl-5 space-y-2">
        {requirementsList.map((req, index) => (
          <li key={index} className="text-gray-700">
            {req}
          </li>
        ))}
      </ul>
    );
  };

  if (loading || translating)
    return <div className="text-center mt-8">
      {translating ? t('jobs.details.translating') : t('jobs.details.loading')}
    </div>;
  if (error)
    return <div className="text-center mt-8 text-red-600">{error}</div>;
  if (!job) return <div className="text-center mt-8">{t('jobs.details.notFound')}</div>;

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      <main className="flex-grow p-8">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/jobs"
            className="inline-flex items-center text-primary hover:text-primary-dark mb-6"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {t('jobs.details.backToJobs')}
          </Link>
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h1 className="text-4xl font-bold mb-2">{getLocalizedContent(job.title)}</h1>
                <p className="text-2xl text-gray-600 mb-2">{getLocalizedContent(job.company)}</p>
                {job.isFeatured && (
                  <span className="bg-yellow-200 text-yellow-800 text-sm font-semibold px-3 py-1 rounded-full mr-2">
                    {t('jobs.details.featured')}
                  </span>
                )}
                <span className="bg-primary-light text-primary-dark text-sm font-semibold px-3 py-1 rounded-full">
                  {job.jobType}
                </span>
              </div>
              <div className="flex space-x-2">
                <button
                  className={`text-gray-500 hover:text-primary ${
                    isSaved ? "text-primary" : ""
                  }`}
                  title={isSaved ? t('jobs.details.jobSaved') : t('jobs.details.saveJob')}
                  onClick={handleSaveJob}
                  disabled={isSaved}
                >
                  <BookmarkPlus className="w-6 h-6" />
                </button>
                <button
                  className="text-gray-500 hover:text-primary"
                  title={t('jobs.details.shareJob')}
                >
                  <Share2 className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {renderField(
                <MapPin className="w-5 h-5 mr-3 text-gray-500" />,
                t('jobs.details.fields.location'),
                job.location
              )}
              {renderField(
                <DollarSign className="w-5 h-5 mr-3 text-gray-500" />,
                t('jobs.details.fields.salary'),
                job.salary
              )}
              {renderField(
                <Clock className="w-5 h-5 mr-3 text-gray-500" />,
                t('jobs.details.fields.posted'),
                new Date(job.postedDate).toLocaleDateString()
              )}
              {renderField(
                <Calendar className="w-5 h-5 mr-3 text-gray-500" />,
                t('jobs.details.fields.deadline'),
                job.applicationDeadline &&
                  new Date(job.applicationDeadline).toLocaleDateString()
              )}
              {renderField(
                <Wifi className="w-5 h-5 mr-3 text-gray-500" />,
                t('jobs.details.fields.remote'),
                job.remoteFriendly ? t('jobs.details.fields.remoteYes') : t('jobs.details.fields.remoteNo')
              )}
              {renderField(
                <Star className="w-5 h-5 mr-3 text-gray-500" />,
                t('jobs.details.fields.experience'),
                job.experienceLevel
              )}
            </div>

            {job.description && (
              <div className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">{t('jobs.details.sections.description')}</h2>
                <p className="text-gray-700 whitespace-pre-line">
                  {getLocalizedContent(job.description)}
                </p>
              </div>
            )}

            {job.requirements && (
              <div className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">{t('jobs.details.sections.requirements')}</h2>
                {typeof job.requirements === 'object' 
                  ? renderRequirements(getLocalizedContent(job.requirements))
                  : renderRequirements(job.requirements)}
              </div>
            )}

            {job.benefits && (
              <div className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">{t('jobs.details.sections.benefits')}</h2>
                <p className="text-gray-700 whitespace-pre-line">
                  {getLocalizedContent(job.benefits)}
                </p>
              </div>
            )}

            {job.skills && job.skills.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">{t('jobs.details.sections.skills')}</h2>
                <div className="flex flex-wrap">
                  {job.skills.map((skill, index) => (
                    <span
                      key={index}
                      className="bg-primary-light text-primary-dark px-3 py-1 rounded-full text-sm mr-2 mb-2"
                    >
                      {getLocalizedContent(skill)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8 flex justify-between items-center">
              <Link
                to={`/apply/${job._id || job.id}`}
                className="bg-primary text-white px-8 py-3 rounded-lg hover:bg-primary-dark transition-colors inline-block text-lg font-semibold"
                onClick={(e) => {
                  console.log('Applying for job with ID:', job._id || job.id);
                }}
              >
                {t('jobs.details.applyNow')}
              </Link>
              <div className="flex items-center text-gray-600">
                <Users className="w-5 h-5 mr-2" />
                <span className="font-semibold">
                  {job.applicationCount || 0}
                </span>{" "}
                {t('jobs.details.applications')}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default JobDetails;
